import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { EnrolAction } from '@/features/publicsite/catalog/EnrolAction';
import { trainingMaterialCount } from '@/features/publicsite/catalog/trainingMaterialCount';
import { trainingSessionAnchor } from '@/features/publicsite/catalog/trainingSessionAnchor';
import { useHashTarget } from '@/features/publicsite/catalog/useHashTarget';
import type { TrainingSession } from '@/features/publicsite/types/TrainingSession';
import { usePublicList } from '@/features/publicsite/usePublicList';
import { calendarLocale, groupByMonth, monthLabel } from '@/features/publicsite/monthCalendar';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import {
  itemTextLang,
  languageFallbackOf,
  servedLanguage,
} from '@/features/publicsite/contentLanguage';
import { usePageLanguageFallback } from '@/features/publicsite/usePageLanguageFallback';
import { useSession } from '@/app/session';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * The training calendar, laid out the same way /evenimente is: months, then a grid of cards with
 * a date box on the left, rather than the plain `.card-grid` the other four catalogue pages share
 * via CatalogSection. A session is a calendar entry before it is a catalogue item — it has a date
 * an event has and a services/funding/community row does not — so it reads the same page, not the
 * generic one, with an enrolment action in place of a link to a detail page (there is none: the
 * card already shows everything a session has).
 */
export function TrainingsPage() {
  const { t, language } = useTranslation(publicCatalog);
  const { user } = useSession();
  const sessions = usePublicList<TrainingSession>(publicCatalogApi.trainingsUrl);
  const [enrolled, setEnrolled] = useState<Set<string>>(new Set());

  // Which of these sessions the visitor is already signed up for, so a second visit shows the
  // enrolled state rather than a button whose only outcome is the backend's 409. One request for
  // the page instead of one per card, and none at all for an anonymous visitor.
  useEffect(() => {
    if (!user) {
      setEnrolled(new Set());
      return;
    }
    let live = true;
    publicCatalogApi
      .myEnrolments()
      .then((rows) => {
        if (live) setEnrolled(new Set(rows.map((r) => r.sessionId)));
      })
      // The calendar is a public page and has to render either way. A visitor whose enrolments
      // fail to load just sees the button, and pressing it still names the reason if there is one.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [user]);

  usePublicPageTitle(t('trainingsHeading'));
  const groups = sessions ? groupByMonth(sessions) : [];
  // A search hit for a session opens /instruiri#sesiune-{id}. The cards arrive after the request
  // does, so the browser's own jump finds nothing; this makes it once they are there.
  useHashTarget(sessions !== null);
  usePageLanguageFallback(languageFallbackOf((sessions ?? []).map(servedLanguage), language));
  // Row by row (T234): a session reads in English where its programme has an English version, so
  // the notice says "partly" for a calendar that holds both and nothing at all once every
  // programme is translated. The institution's name is never translated — it is registered under
  // one name — so it carries no `lang` of its own, as an event's organiser does not.

  return (
    <section className="container section">
      <div className="section-head">
        <div>
          <span className="eyebrow">{t('eyebrowCatalog')}</span>
          <h1>{t('trainingsHeading')}</h1>
          <p className="muted">{t('trainingsLead')}</p>
        </div>
        {/* To the directory, the way /cercetare hands the reader on to its organisations —
            the calendar lists sessions, the institutions behind them live one click away. */}
        <Link className="head-link" to="/instruiri/institutii">
          {t('institutionsLink')}
        </Link>
      </div>

      {groups.length > 0 && (
        <div className="section-body event-list">
          {groups.map(([key, items]) => (
            <div key={key}>
              <h3 className="event-month">{monthLabel(key, language)}</h3>
              <div className="cal-card">
                {items.map((s) => {
                  const d = new Date(s.startsAt);
                  const materialCount = trainingMaterialCount(s);
                  return (
                    <div
                      className="cal-item"
                      key={s.sessionId}
                      id={trainingSessionAnchor(s.sessionId)}
                      tabIndex={-1}
                    >
                      <span className="cal-item__date">
                        <strong>{String(d.getDate()).padStart(2, '0')}</strong>
                        <span>
                          {d.toLocaleDateString(calendarLocale(language), { month: 'short' })}
                        </span>
                      </span>
                      <span className="cal-item__main">
                        <span className="cal-item__cat">
                          {s.online ? t('trainingsOnline') : t('trainingsOnSite')}
                        </span>
                        <span
                          className="cal-item__title"
                          lang={itemTextLang(s, 'programTitle', language)}
                        >
                          {s.programTitle}
                        </span>
                        <span className="cal-item__meta">
                          {/* The institution's own profile, where its domains, staff and the
                              rest of its portfolio are. A session from before institutionId
                              existed carries none, and stays plain text. */}
                          {s.institutionId ? (
                            <Link to={`/instruiri/institutii/${s.institutionId}`}>
                              {s.institution}
                            </Link>
                          ) : (
                            <span>{s.institution}</span>
                          )}
                          {s.location && (
                            <span lang={itemTextLang(s, 'location', language)}>{s.location}</span>
                          )}
                        </span>
                        {materialCount !== null && (
                          <span className="cal-item__materials">
                            {t('trainingsMaterialCount', { count: materialCount })}
                          </span>
                        )}
                        <span className="cal-item__action">
                          <EnrolAction
                            sessionId={s.sessionId}
                            signedIn={!!user}
                            enrolled={enrolled.has(s.sessionId)}
                            onEnrolled={(id) => setEnrolled((prev) => new Set(prev).add(id))}
                          />
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {sessions && groups.length === 0 && <p className="empty-state">{t('emptyState')}</p>}
    </section>
  );
}
